import { PDFDocument, rgb } from "pdf-lib";
import { standardFontForKey } from "helpers/pdfFontMapping";

const UNICODE_TO_WIN_ANSI = {
    "\u2018": "'",
    "\u2019": "'",
    "\u201a": "'",
    "\u201b": "'",
    "\u201c": '"',
    "\u201d": '"',
    "\u201e": '"',
    "\u2013": "-",
    "\u2014": "-",
    "\u2022": "*",
    "\u2026": "...",
};

const LINE_HEIGHT_FACTOR = 1.15;
const ASCENT_RATIO = 0.78;
const DESCENT_RATIO = 0.22;
const ERASE_PAD_X = 3;
const ERASE_PAD_Y = 2;

/** pdf-lib StandardFonts only support WinAnsi; strip/replace other characters to avoid save errors. */
export const sanitizePdfDrawText = (text = "") => {
    let normalized = String(text ?? "");
    Object.entries(UNICODE_TO_WIN_ANSI).forEach(([from, to]) => {
        normalized = normalized.split(from).join(to);
    });
    return normalized.replace(/[^\x00-\xFF]/g, "");
};

export const normalizePdfSourceBytes = (bytes) => {
    if (!bytes) {
        throw new Error("PDF data is missing");
    }
    if (bytes instanceof Uint8Array) {
        return new Uint8Array(bytes);
    }
    if (bytes instanceof ArrayBuffer) {
        return new Uint8Array(bytes);
    }
    throw new Error("Unsupported PDF data format");
};

const parseColor = (item, prefix, fallback) => {
    const r = Number(item[`${prefix}R`]);
    const g = Number(item[`${prefix}G`]);
    const b = Number(item[`${prefix}B`]);
    if ([r, g, b].every((v) => Number.isFinite(v))) {
        return rgb(
            Math.max(0, Math.min(1, r)),
            Math.max(0, Math.min(1, g)),
            Math.max(0, Math.min(1, b))
        );
    }
    return fallback;
};

/**
 * PDF user-space erase bounds: baseline at y, cover original glyphs and any new lines.
 */
const computeEraseBounds = ({
    x,
    y,
    fontSize,
    originalWidth,
    originalHeight,
    lineCount,
    lineHeight,
    maxLineWidth,
}) => {
    const origW = Math.max(Number(originalWidth) || 0, fontSize * 0.35);
    const origH = Math.max(Number(originalHeight) || 0, fontSize * (ASCENT_RATIO + DESCENT_RATIO));

    const newTextHeight =
        lineCount <= 1
            ? fontSize * (ASCENT_RATIO + DESCENT_RATIO)
            : fontSize * ASCENT_RATIO + (lineCount - 1) * lineHeight + fontSize * DESCENT_RATIO;

    const eraseWidth = Math.max(origW, maxLineWidth) + ERASE_PAD_X * 2;
    const eraseHeight = Math.max(origH, newTextHeight) + ERASE_PAD_Y * 2;

    const bottomY =
        y -
        (lineCount - 1) * lineHeight -
        fontSize * DESCENT_RATIO -
        ERASE_PAD_Y;

    return {
        x: x - ERASE_PAD_X,
        y: bottomY,
        width: eraseWidth,
        height: eraseHeight,
    };
};

/**
 * Apply in-preview text edits: paint over the original glyph area, then draw replacement text.
 */
export const applyPdfTextReplacements = async (
    sourceBytes,
    replacements = [],
    options = {}
) => {
    const normalizedBytes = normalizePdfSourceBytes(sourceBytes);
    const pdfDoc = await PDFDocument.load(normalizedBytes, {
        ignoreEncryption: true,
        updateMetadata: false,
    });

    const embeddedFonts = new Map();
    const getFont = async (pdfFontKey) => {
        const key = pdfFontKey || "Helvetica";
        if (!embeddedFonts.has(key)) {
            const standard = standardFontForKey(key);
            embeddedFonts.set(key, await pdfDoc.embedFont(standard));
        }
        return embeddedFonts.get(key);
    };

    const pageCount = pdfDoc.getPageCount();

    const valid = (replacements || []).filter((item) => {
        if (!item || Number(item.page) < 1 || Number(item.page) > pageCount) {
            return false;
        }
        const newTextRaw = String(item.newText ?? "");
        const newText = newTextRaw.trim();
        if (item.isInsert) {
            return Boolean(newText);
        }
        if (!newText) {
            return newTextRaw !== String(item.originalText ?? "");
        }
        if (newText !== String(item.originalText ?? "")) return true;
        return Boolean(item.styleChanged);
    });

    for (const item of valid) {
        const page = pdfDoc.getPage(Number(item.page) - 1);
        const fontSize = Math.max(
            6,
            Math.min(96, Number(item.fontSize) || 12)
        );
        const x = Number(item.x) || 0;
        const y = Number(item.y) || 0;
        const rawText = sanitizePdfDrawText(item.newText);
        const lineList = rawText.split(/\r?\n/);
        const lineHeight = fontSize * LINE_HEIGHT_FACTOR;

        const font = await getFont(item.pdfFontKey);
        let maxLineWidth = 0;
        lineList.forEach((line) => {
            const trimmed = String(line ?? "");
            if (!trimmed) return;
            maxLineWidth = Math.max(
                maxLineWidth,
                font.widthOfTextAtSize(trimmed, fontSize)
            );
        });

        const erase = computeEraseBounds({
            x,
            y,
            fontSize,
            originalWidth: item.width,
            originalHeight: item.height,
            lineCount: lineList.length,
            lineHeight,
            maxLineWidth,
        });

        const fillColor = parseColor(item, "fillColor", rgb(1, 1, 1));
        const textColor = parseColor(item, "textColor", rgb(0, 0, 0));

        if (!item.isInsert) {
            page.drawRectangle({
                x: erase.x,
                y: erase.y,
                width: erase.width,
                height: erase.height,
                color: fillColor,
                borderWidth: 0,
            });

            page.drawRectangle({
                x: erase.x - 0.5,
                y: erase.y - 0.5,
                width: erase.width + 1,
                height: erase.height + 1,
                color: fillColor,
                borderWidth: 0,
            });
        }

        lineList.forEach((line, lineIndex) => {
            const trimmed = String(line ?? "");
            if (!trimmed) return;
            page.drawText(trimmed, {
                x,
                y: y - lineIndex * lineHeight,
                size: fontSize,
                font,
                color: textColor,
            });
        });
    }

    const outputFileName = String(options?.outputFileName || "").trim();
    if (outputFileName) {
        try {
            pdfDoc.setTitle(outputFileName.replace(/\.pdf$/i, ""));
        } catch {
            // ignore metadata errors on encrypted PDFs
        }
    }

    return pdfDoc.save();
};

/** Apply edits; always vector PDF (flatten ignored — keeps click-to-edit on reopen). */
export const buildSavedPdfBytes = async (sourceBytes, replacements = [], options = {}) => {
    const { flatten: _flattenIgnored, flattenScale: _scaleIgnored, ...safeOptions } =
        options || {};
    return applyPdfTextReplacements(sourceBytes, replacements, safeOptions);
};
